import { Button } from "~/ui-rtk/components/ui/controls"
import { formatNumber } from "~/ui-rtk/utils/format"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import type { RowsSummary } from "../budget-utils"
import type { AllocationSummary, PoolTotals } from "../allocation"

interface ActionBarProps {
  selectedCount: number
  /** Totals for the rows currently shown; null hides the totals (no rows) */
  summary: RowsSummary | null
  /** Rows hidden by the zero-spend filter, so the totals say what they cover */
  hiddenRowCount?: number
  /**
   * The allocation pool: what the unchecked rows commit, what is left for
   * the checked ones, and whether the result lands on target. Hidden when
   * absent (no rows yet).
   */
  allocation?: AllocationSummary | null
  /** The pool as it will be applied — hand edits folded in. */
  pool?: PoolTotals | null
  onReview: () => void
  onCancel: () => void
}

function changeColorClass(pct: number): string {
  if (pct > 0) return "text-american-green"
  if (pct < 0) return "text-states-error"
  return "text-gray-400"
}

function Stat({
  label,
  value,
  className,
}: {
  label: string
  value: string
  className?: string
}) {
  return (
    <div className="flex flex-col leading-tight">
      <dt className="text-[10px] uppercase tracking-wider text-gray-400">
        {label}
      </dt>
      <dd className={cn("text-sm font-semibold text-white", className)}>
        {value}
      </dd>
    </div>
  )
}

/** Rounding noise, not a real miss, when the total lands within this of the target. */
const ON_TARGET_TOLERANCE = 1

/**
 * One line that says where the target goes: how much the unchecked rows
 * keep, how much is left for the checked ones, and whether the total after
 * approval lands on, under, or over the target. The scale-down-only rule
 * means headroom is common (recommendations are never inflated) and the
 * carve-out means over-commitment is possible (unchecked rows alone can
 * exceed the target) — both must be visible before Review, not after.
 */
export function PoolLine({
  allocation,
  pool,
}: {
  allocation: AllocationSummary
  pool: PoolTotals
}) {
  const money = (n: number) => formatNumber(n, { currency: true })
  const status = allocation.overCommitted
    ? {
        tone: "text-states-error",
        text: `Unchecked rows alone exceed the target — selected rows scale toward $0. Check more rows or raise the target.`,
      }
    : pool.headroom > ON_TARGET_TOLERANCE
      ? {
          tone: "text-yellow-300",
          text: `Lands ${money(pool.headroom)} under target: recommendations are never scaled above what performance supports.`,
        }
      : pool.headroom < -ON_TARGET_TOLERANCE
        ? {
            tone: "text-states-error",
            text: `Lands ${money(-pool.headroom)} over target.`,
          }
        : { tone: "text-gray-400", text: "Lands on target." }

  return (
    <div
      className="text-xs text-gray-400 flex flex-wrap items-center gap-x-2 gap-y-0.5"
      data-testid="pool-line"
    >
      <span>
        Target{" "}
        <span className="text-gray-200">
          {money(allocation.targetDailyBudget)}
        </span>
      </span>
      <span aria-hidden="true">·</span>
      <span>
        <span className="text-gray-200">{money(pool.committed)}</span> stays as
        is ({pool.uncheckedCount} unchecked{" "}
        {pool.uncheckedCount === 1 ? "row" : "rows"}
        {allocation.unassignedCount > 0 &&
          `, ${allocation.unassignedCount} without a type`}
        {allocation.lockedCount > 0 &&
          `, ${allocation.lockedCount} pinned or held by the brand-search policy`}
        )
      </span>
      <span aria-hidden="true">·</span>
      <span>
        <span className="text-gray-200">{money(pool.checkedNew)}</span> to{" "}
        {pool.checkedCount} selected
        {pool.checkedCount > 0 &&
          (allocation.brand.scale < 1 || allocation.dr.scale < 1) && (
            <span title="Raw recommendations scaled down to fit what the unchecked rows leave of each category's share">
              {" "}
              (scaled to{" "}
              {[
                allocation.brand.checkedCount > 0 &&
                  `brand ${Math.round(allocation.brand.scale * 100)}%`,
                allocation.dr.checkedCount > 0 &&
                  `DR ${Math.round(allocation.dr.scale * 100)}%`,
              ]
                .filter(Boolean)
                .join(", ")}
              )
            </span>
          )}
      </span>
      {allocation.stepCappedCount > 0 && (
        <>
          <span aria-hidden="true">·</span>
          <span
            className="text-sky-300"
            title="The step cap clamps each checked row to current × (1 ± cap). The difference shows up as headroom (increases held back) or over target (cuts held back)."
          >
            {allocation.stepCappedCount} held by the step cap (
            {allocation.stepCapAdjustment >= 0 ? "+" : "−"}
            {money(Math.abs(allocation.stepCapAdjustment))})
          </span>
        </>
      )}
      <span aria-hidden="true">·</span>
      <span className={status.tone}>{status.text}</span>
    </div>
  )
}

/**
 * Always-visible bottom bar. The recommended / adjusted budget totals stay in
 * view without scrolling to the table footer on large accounts, the pool
 * line says how the target is being split, and the review actions join them
 * once rows are selected.
 */
export default function ActionBar({
  selectedCount,
  summary,
  hiddenRowCount = 0,
  allocation,
  pool,
  onReview,
  onCancel,
}: ActionBarProps) {
  if (selectedCount === 0 && summary === null) return null

  const pct = summary?.aggPctChange ?? 0

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-system-blue-800 border-t border-basic-blue px-6 py-3">
      <div className="max-w-screen-xl mx-auto flex items-center justify-between gap-6">
        {summary ? (
          <div className="flex flex-col gap-1 min-w-0">
            <div className="flex items-center gap-6">
              <dl
                className="flex items-center gap-6"
                aria-label="Budget totals"
              >
                <Stat
                  label="Current"
                  value={formatNumber(summary.totalCurrent, { currency: true })}
                />
                <Stat
                  label="Recommended"
                  value={formatNumber(summary.totalRawRecommended, {
                    currency: true,
                  })}
                  className="text-gray-300"
                />
                <Stat
                  label="Adjusted"
                  value={formatNumber(summary.totalAdjusted, {
                    currency: true,
                  })}
                />
                <Stat
                  label="% Change"
                  value={`${pct > 0 ? "+" : ""}${pct.toFixed(1)}%`}
                  className={changeColorClass(pct)}
                />
              </dl>
              {hiddenRowCount > 0 && (
                <span
                  className="text-xs text-gray-400"
                  title="Totals cover the rows currently shown"
                >
                  {hiddenRowCount} zero-spend{" "}
                  {hiddenRowCount === 1 ? "row" : "rows"} hidden
                </span>
              )}
            </div>
            {allocation && pool && (
              <PoolLine allocation={allocation} pool={pool} />
            )}
          </div>
        ) : (
          <span />
        )}

        <div className="flex items-center gap-3 shrink-0">
          {selectedCount > 0 ? (
            <>
              <span className="text-white text-sm">
                {selectedCount} {selectedCount === 1 ? "entity" : "entities"}{" "}
                selected
              </span>
              <Button
                variant={{ variant: "outlined" }}
                size="sm"
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button
                variant={{ variant: "solid", color: "blue" }}
                size="sm"
                onClick={onReview}
              >
                Review Changes
              </Button>
            </>
          ) : (
            <span className="text-xs text-gray-400">
              Select rows to review changes
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
